/**
 * 원격지원 담당자 뷰어 창 ↔ 메인 창 링크
 *
 * 담당자의 원격 화면은 채팅 앱 안이 아니라 전용 창(`?rsviewer=<nonce>` — RemoteViewerWindow)에 뜬다.
 *
 *  - **소켓은 메인 창에만 있다.** 서버는 세션 라우팅을 담당자 socketId에 고정하므로(remoteSupportSessions.js)
 *    뷰어 창이 소켓을 따로 열면 "같은 계정의 다른 기기"로 무시된다. 메인(RemoteSupportOperator)이 세션 상태 머신을
 *    그대로 돌리며 상태 스냅샷을 보내고, 뷰어의 명령·시그널을 소켓으로 옮긴다
 *  - **RsPeer는 뷰어 창이 가진다.** MediaStream은 창 사이로 넘길 수 없고, 메인 창이 숨거나(트레이) 최소화되면
 *    그쪽 타이머가 1초~1분으로 스로틀링되므로 영상·입력이 메인에 묶이면 안 된다. 메인은 rs:signal만 중계한다
 *  - 전송은 BroadcastChannel 하나 — 웹 팝업과 Tauri 창(같은 origin·같은 WebView2 프로필) 공통.
 *    다른 탭도 같은 채널을 들으므로 세션마다 무작위 nonce로 거른다
 *  - 뷰어는 서버를 직접 부르지 않는다(메모 저장도 메인이 대행)
 *
 * 창 열기: 웹은 window.open(사용자 제스처 안에서 동기로 — 팝업 차단), PC앱은 Rust 커맨드
 * (Tauri에서 window.open은 아무것도 열지 않는다 — src-tauri/src/remote_support.rs '담당자 뷰어 창').
 */
import type { ToastType } from '../components/Toast';
import type { RemoteInputEvent } from '../utils/remoteInput';
import { isTauri } from '../utils/tauri';
import { PcSummary, RemoteSupportTarget, RsSignal, rsInvoke } from './remoteSupportService';

// ─────────────────────────── 프로토콜 ───────────────────────────

export type ViewerPhase = 'opening' | 'waiting' | 'picking' | 'viewing' | 'ended';
export type ViewerControl = 'none' | 'requested' | 'granted';

export interface ViewerSummary {
  code: string | null;
  hostName: string;
  durationSeconds: number | null;
  controlSeconds: number;
  reason: string;
  endedBy: string | null;
  /** 주고받은 파일 수(서버 rs:ended). 담당자 → 직원 / 직원 → 담당자 */
  filesToHost?: number;
  filesToOperator?: number;
}

export interface ViewerSnapshot {
  phase: ViewerPhase;
  /** 직원이 먼저 요청해 담당자가 수락한 세션 — 대기 문구만 바뀐다 */
  asked: boolean;
  target: RemoteSupportTarget | null;
  code: string | null;
  controlEnabled: boolean;
  control: ViewerControl;
  pcInfo: PcSummary | null;
  secureDesktop: boolean;
  /** 절대 시각(ms) — 같은 PC라 시계를 공유한다. 초 카운트다운은 뷰어가 직접 돈다 */
  expiresAt: number | null;
  startedAt: number | null;
  /** phase='ended': 화면을 본 세션이면 요약, 시작 전에 끝났으면 안내 문구 */
  summary: ViewerSummary | null;
  endedMessage: string | null;
  /** 파일 주고받기 정책(메인의 /me 스토어). 실제로 쓸 수 있는지는 창이 'fx' 채널까지 보고 정한다 */
  fileTransferEnabled: boolean;
}

export type ViewerCommand =
  | { name: 'request-control' }
  | { name: 'return-control' }
  | { name: 'end' }
  | { name: 'open-dm' }
  | { name: 'save-note'; note: string };

export type MainToViewer =
  | { type: 'state'; snap: ViewerSnapshot }
  | { type: 'signal'; data: RsSignal }
  | { type: 'toast'; toastType: ToastType; message: string }
  /** PC앱 창의 × — 뷰어가 확인창을 띄운다 */
  | { type: 'close-requested' }
  | { type: 'note-result'; ok: boolean; message?: string }
  | { type: 'pong' }
  /** 메인이 사라진다(새로고침·로그아웃) */
  | { type: 'bye' };

export type ViewerToMain =
  /** 뷰어가 떴다 — 메인이 스냅샷과 그동안 모아 둔 시그널을 보낸다 */
  | { type: 'hello' }
  | { type: 'signal'; data: RsSignal }
  /** DataChannel이 안 열렸을 때의 입력 — 메인이 rs:input(서버 중계)으로 보낸다 */
  | { type: 'input'; events: RemoteInputEvent[] }
  | { type: 'cmd'; cmd: ViewerCommand }
  | { type: 'close-ack' }
  | { type: 'ping' }
  /** 뷰어가 닫힌다(pagehide) */
  | { type: 'closing' };

const CHANNEL_NAME = 'ina-rs-viewer';
const VERSION = 1;
const NONCE_RE = /^[A-Za-z0-9-]{8,64}$/;

interface Envelope {
  v: number;
  nonce: string;
  from: 'main' | 'viewer';
  msg: unknown;
}

export interface ViewerLink<Out> {
  post(msg: Out): void;
  close(): void;
}

function openLink<Out, In>(nonce: string, from: 'main' | 'viewer', onMessage: (msg: In) => void): ViewerLink<Out> {
  let ch: BroadcastChannel | null = null;
  try {
    ch = new BroadcastChannel(CHANNEL_NAME);
  } catch (err) {
    console.error('[RemoteViewer] BroadcastChannel unavailable:', err);
  }
  if (ch) {
    ch.onmessage = (e: MessageEvent) => {
      const d = e.data as Envelope | null;
      // 다른 세션(다른 탭·지난 창)과 자기 자신이 보낸 것은 버린다
      if (!d || d.v !== VERSION || d.nonce !== nonce || d.from === from || !d.msg) return;
      onMessage(d.msg as In);
    };
  }
  return {
    post(msg: Out) {
      try {
        const env: Envelope = { v: VERSION, nonce, from, msg };
        ch?.postMessage(env);
      } catch (err) {
        console.warn('[RemoteViewer] post failed:', err);
      }
    },
    close() {
      ch?.close();
      ch = null;
    },
  };
}

export const openMainLink = (nonce: string, onMessage: (msg: ViewerToMain) => void) =>
  openLink<MainToViewer, ViewerToMain>(nonce, 'main', onMessage);

export const openViewerLink = (nonce: string, onMessage: (msg: MainToViewer) => void) =>
  openLink<ViewerToMain, MainToViewer>(nonce, 'viewer', onMessage);

/** 이 페이지가 뷰어 창이면 nonce, 아니면 null. App이 모든 훅보다 먼저 분기한다 */
export function getRemoteViewerNonce(): string | null {
  try {
    const n = new URLSearchParams(window.location.search).get('rsviewer');
    return n && NONCE_RE.test(n) ? n : null;
  } catch {
    return null;
  }
}

export function createViewerNonce(): string {
  try {
    if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  } catch {
    /* 비보안 컨텍스트 */
  }
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

// ─────────────────────────── 창 (메인 쪽) ───────────────────────────

export interface ViewerWindowHandle {
  focus(): void;
  close(): void;
  /** 웹: 팝업이 닫혔는가. PC앱은 Rust가 rs-viewer-closed로 알리므로 늘 false */
  isClosed(): boolean;
}

/**
 * 뷰어 창을 연다. ⚠ 웹은 사용자 제스처 안에서 **await 전에 동기로** 불러야 한다(팝업 차단).
 * 웹에서 차단되면 null. PC앱은 생성 실패를 onFail로 알린다(비동기).
 */
export function openViewerWindow(nonce: string, title: string, onFail: () => void): ViewerWindowHandle | null {
  if (isTauri()) {
    rsInvoke('rs_viewer_open', { nonce, title }).catch((err) => {
      console.error('[RemoteViewer] rs_viewer_open failed:', err);
      onFail();
    });
    return {
      focus: () => void rsInvoke('rs_viewer_focus').catch(() => {}),
      close: () => void rsInvoke('rs_viewer_close').catch(() => {}),
      isClosed: () => false,
    };
  }
  // noopener를 주면 핸들이 null이라 차단을 구분할 수 없고 '대화 열기'가 opener를 앞으로 가져올 수 없다
  const w = window.open(
    `${window.location.origin}/?rsviewer=${encodeURIComponent(nonce)}`,
    'ina-rs-viewer',
    'popup=yes,width=1280,height=800'
  );
  if (!w) return null;
  return {
    focus: () => {
      try {
        w.focus();
      } catch {
        /* 닫힘 */
      }
    },
    close: () => {
      try {
        w.close();
      } catch {
        /* 닫힘 */
      }
    },
    isClosed: () => w.closed,
  };
}

/** 메인 창 부팅 시 1회(PC앱) — 웹뷰 새로고침·업데이트 재시작이 남긴 뷰어 창을 걷는다 */
export function closeStaleViewerWindow() {
  if (isTauri()) rsInvoke('rs_viewer_close').catch(() => {});
}

// ─────────────────────────── 창 (뷰어 쪽) ───────────────────────────

/** 뷰어 창이 스스로 닫힌다. PC앱 창은 core 권한이 없어 Rust 커맨드로 닫는다 */
export function closeViewerSelf() {
  if (isTauri()) {
    rsInvoke('rs_viewer_close').catch(() => window.close());
  } else {
    window.close();
  }
}

/** PC앱 ×를 받아 확인창을 띄웠다 — Rust의 '응답 없는 × 두 번이면 닫기' 안전판을 거둔다 */
export function markViewerCloseHandled() {
  if (isTauri()) rsInvoke('rs_viewer_close_handled').catch(() => {});
}
